import fp from "lodash/fp";

import {
	ChargebackInformationNameFields as cin,
	ChargebackRecommendationNameFields as crn,
	ChargebacksQueueFields,
} from "globalConstants";
import { getDateWithoutTime, safeParseFloat } from "helpers";
import {
	ChargebacksQueue,
	ChargebacksFetchDataParams,
	ChargebackDecisions,
} from "types";

const PAGE_SIZE = 50;

const {
	Id,
	AssignedTo,
	AssigneeId,
	ChargebackTransactionsPercent,
	ChargebackVolumePercent,
	ChargebacksAmount,
	ChargebacksCount,
	MerchantId,
	MerchantName,
	MerchantPriorityType,
	Mid,
	ReviewType,
	SalesMonthlyVolume,
	TransactionsCount,
	MonthlyVolumesNumberFrom,
	MonthlyVolumesNumberTo,
	TransactionsCountFrom,
	TransactionsCountTo,
	ChargebacksCountFrom,
	ChargebacksCountTo,
	ChargebacksAmountFrom,
	ChargebacksAmountTo,
} = ChargebacksQueueFields;

export const mapChargebacksQueueDataFromServer = (data): ChargebacksQueue => {
	return {
		[Id.name]: data[Id.beName],
		[AssignedTo.name]: data[AssignedTo.beName],
		[AssigneeId.name]: data[AssigneeId.beName],
		[ChargebackTransactionsPercent.name]:
			data[ChargebackTransactionsPercent.beName],
		[ChargebackVolumePercent.name]: data[ChargebackVolumePercent.beName],
		[ChargebacksAmount.name]: data[ChargebacksAmount.beName],
		[ChargebacksCount.name]: data[ChargebacksCount.beName],
		[MerchantId.name]: data[MerchantId.beName],
		[MerchantName.name]: data[MerchantName.beName],
		[MerchantPriorityType.name]: data[MerchantPriorityType.beName],
		[Mid.name]: data[Mid.beName],
		[ReviewType.name]: data[ReviewType.beName],
		[SalesMonthlyVolume.name]: data[SalesMonthlyVolume.beName],
		[TransactionsCount.name]: data[TransactionsCount.beName],
	};
};

const mapSortBy = sortBy => {
	switch (sortBy) {
		case MerchantName.name:
			return MerchantName.beName;
		case SalesMonthlyVolume.name:
			return SalesMonthlyVolume.beName;
		case TransactionsCount.name:
			return TransactionsCount.beName;
		case ChargebacksCount.name:
			return ChargebacksCount.beName;
		case ChargebacksAmount.name:
			return ChargebacksAmount.beName;
		case ChargebackTransactionsPercent.name:
			return ChargebackTransactionsPercent.beName;
		case ChargebackVolumePercent.name:
			return ChargebackVolumePercent.beName;
		default:
			return SalesMonthlyVolume.beName;
	}
};

export const mapChargebacksQueueDataToServer = ({
	page,
	sortBy,
	filters,
}: ChargebacksFetchDataParams) => {
	const _filters = fp.cloneDeep(filters);
	return {
		page_index: page,
		page_size: PAGE_SIZE,
		sort_type: mapSortBy(sortBy?.id),
		ascending: sortBy?.id ? sortBy?.desc : false,
		[MerchantId.beName]: _filters?.mid?.map(item => item?.id),
		[MonthlyVolumesNumberFrom.beName]: safeParseFloat(
			_filters?.monthlyVolumesNumberFrom,
		),
		[MonthlyVolumesNumberTo.beName]: safeParseFloat(
			_filters?.monthlyVolumesNumberTo,
		),
		[TransactionsCountFrom.beName]: safeParseFloat(
			_filters?.transactionsCountFrom,
		),
		[TransactionsCountTo.beName]: safeParseFloat(
			_filters?.transactionsCountTo,
		),
		[ChargebacksCountFrom.beName]: safeParseFloat(
			_filters?.chargebacksCountFrom,
		),
		[ChargebacksCountTo.beName]: safeParseFloat(
			_filters?.chargebacksCountTo,
		),
		[ChargebacksAmountFrom.beName]: safeParseFloat(
			_filters?.chargebacksAmountFrom,
		),
		[ChargebacksAmountTo.beName]: safeParseFloat(
			_filters?.chargebacksAmountTo,
		),
		[ReviewType.beName]: _filters?.reviewType?.name,
		[AssigneeId.beName]: _filters?.assignedTo?.id,
	};
};

export const getMappedChargebacksRecommendationData = ({
	recommendation,
	decision,
}) => {
	return {
		[crn.recommendation]: recommendation,
		[crn.decision]: ChargebackDecisions.getByName(decision),
	};
};

export const mappedChargebacksRecommendationData = formValues => {
	return {
		recommendation: formValues?.[crn.recommendation],
		decision: formValues?.[crn.decision]?.name,
	};
};

export const getMappedChargebacksInfoData = data => {
	return {
		[cin.reasonForReview]: data?.reason_for_review,
		[cin.reductionPlan]: data?.reduction_plan,
		[cin.reductionPlanDescription]: data?.reduction_plan_description,
		[cin.documentReview]: data?.document_review,
		[cin.followUpDate]: data?.follow_up_date,
		[cin.nextChargebackReviewDate]: data?.next_chargeback_review_date,
		[cin.assigneeId]: data?.assignee_id,
	};
};

export const mappedChargebacksInfoData = formValues => {
	return {
		reason_for_review: formValues?.[cin.reasonForReview] || "",
		reduction_plan: formValues?.[cin.reductionPlan],
		reduction_plan_description:
			formValues?.[cin.reductionPlanDescription] || "",
		document_review: formValues?.[cin.documentReview] || "",
		follow_up_date: getDateWithoutTime(formValues?.[cin.followUpDate]),
		next_chargeback_review_date: getDateWithoutTime(
			formValues?.[cin.nextChargebackReviewDate],
		),
	};
};
